import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Schedule from "./pages/Schedule";
import Absences from "./pages/Absences";
import Users from "./pages/Users";
import Team from "./pages/Team";
import Stats from "./pages/Stats";
import ReportingPlanning from "./pages/ReportingPlanning";
import LeaveManagement from "./pages/LeaveManagement";
import Admin from "./pages/Admin";
import Profile from "./pages/Profile";
import TeamCommunication from "./pages/TeamCommunication";
import Help from "./pages/Help";
import axios from "axios";
import { logError } from "./services/errorLogger";

// Setup axios interceptor to log all errors
axios.interceptors.response.use(
  response => response,
  error => {
    const errorMessage = error.response?.data?.detail || error.message || 'Erreur réseau';
    const url = error.config?.url || 'unknown';
    logError(errorMessage, 'error', 'api', { url, status: error.response?.status });
    return Promise.reject(error);
  }
);

// Public pages
import Fitness from "./pages/public/Fitness";
import Racket from "./pages/public/Racket";
import Wellness from "./pages/public/Wellness";
import UeberUns from "./pages/public/UeberUns";
import Kontakt from "./pages/public/Kontakt";

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Extranet as default */}
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      
      {/* Public website pages */}
      <Route path="/site" element={<Home />} />
      <Route path="/fitness" element={<Fitness />} />
      <Route path="/racket" element={<Racket />} />
      <Route path="/wellness" element={<Wellness />} />
      <Route path="/ueber-uns" element={<UeberUns />} />
      <Route path="/kontakt" element={<Kontakt />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
      <Route path="/absences" element={<ProtectedRoute><Absences /></ProtectedRoute>} />
      
      <Route path="/team" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Team /></ProtectedRoute>} />
      <Route path="/stats" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Stats /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute><ReportingPlanning /></ProtectedRoute>} />
      <Route path="/leave" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><LeaveManagement /></ProtectedRoute>} />
      <Route path="/communicate" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><TeamCommunication /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Help /></ProtectedRoute>} />
      
      <Route path="/users" element={<ProtectedRoute allowedRoles={["directeur"]}><Users /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["directeur"]}><Admin /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
